import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Shared behaviour for the horizontal card sliders (properties): a native
 * snap-scrolling `<ul class="scroll-track">` plus prev/next arrows that
 * page by ~85% of the visible width and grey out at either end. The arrow
 * state is re-derived on scroll, resize and whenever the rendered item
 * count changes.
 */
export function useScrollTrack<T extends HTMLElement = HTMLUListElement>(deps: unknown[] = []) {
  const trackRef = useRef<T>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanPrev(scrollLeft > 4);
    setCanNext(scrollLeft < scrollWidth - clientWidth - 4);
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, ...deps]);

  const scrollByDir = useCallback((dir: "prev" | "next") => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({
      left: dir === "next" ? amount : -amount,
      behavior: reduce ? "auto" : "smooth",
    });
  }, []);

  return { trackRef, canPrev, canNext, scrollByDir, onScroll: update };
}

interface MarqueeOptions {
  /** Drift speed in CSS px per second. */
  speed?: number;
  /** `1` drifts the cards leftwards (the usual reading direction), `-1` rightwards. */
  direction?: 1 | -1;
}

/** How long after the user last touched the track before it drifts again. */
const MARQUEE_RESUME_MS = 3000;

/**
 * Continuously drifts a `<ul class="marquee-track">` whose items have
 * been repeated (see `MarqueeTrack`), wrapping by one copy's width so
 * the loop never visibly ends. The drift glides to a stop while the
 * pointer hovers, a finger or keyboard focus is on the track, the tab is
 * hidden or the track is off-screen, and never runs for users who ask
 * for reduced motion. The user's own scrolling is left alone; the drift
 * resumes a few seconds after they stop.
 */
export function useMarquee(
  { speed = 40, direction = 1 }: MarqueeOptions = {},
  deps: unknown[] = [],
) {
  const trackRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    // Reasons the drift is currently held; it runs only when this is empty.
    const holds = new Set<string>();
    let resumeTimer = 0;
    let loop = 0; // width of one copy of the list
    let pos = el.scrollLeft; // fractional scroll position the drift owns
    let velocity = 0;
    let last = 0;
    let raf = 0;
    let running = false;

    const measure = () => {
      const [first, second] = Array.from(
        el.querySelectorAll<HTMLElement>(":scope > [data-loop-start]"),
      );
      loop = first && second ? second.offsetLeft - first.offsetLeft : 0;
      // Wrapping jumps by one copy; that is only invisible while a whole
      // copy still fits inside the scrollable range.
      if (loop <= 0 || loop > el.scrollWidth - el.clientWidth) loop = 0;
      if (loop && holds.size === 0) run();
    };

    const normalize = (p: number) => (loop ? ((p % loop) + loop) % loop : p);

    const write = (p: number) => {
      pos = p;
      el.scrollLeft = p;
    };

    const frame = (t: number) => {
      const dt = Math.min(64, t - (last || t));
      last = t;
      const target = holds.size === 0 && loop ? speed * direction : 0;
      // Ease toward the target speed so pauses and resumes glide.
      velocity += (target - velocity) * Math.min(1, dt / 220);
      if (target === 0 && Math.abs(velocity) < 0.5) {
        // Fully stopped: give the frame loop up until a hold is released.
        velocity = 0;
        running = false;
        return;
      }
      write(normalize(pos + (velocity * dt) / 1000));
      raf = requestAnimationFrame(frame);
    };

    const run = () => {
      if (running) return;
      running = true;
      last = 0;
      pos = el.scrollLeft;
      raf = requestAnimationFrame(frame);
    };

    const hold = (reason: string) => {
      holds.add(reason);
    };
    const release = (reason: string) => {
      holds.delete(reason);
      if (holds.size === 0) run();
    };

    const syncMotionHold = () => {
      if (reduceMotion.matches) hold("motion");
      else release("motion");
    };

    const holdUntilIdle = () => {
      hold("user");
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        // Wrap whatever position the user left us at before drifting on.
        if (loop) write(normalize(el.scrollLeft));
        release("user");
      }, MARQUEE_RESUME_MS);
    };

    const onScroll = () => {
      // A position we did not write means the user is dragging, flicking
      // or wheeling: stop pushing against them.
      if (Math.abs(el.scrollLeft - pos) > 2) {
        velocity = 0;
        pos = el.scrollLeft;
        holdUntilIdle();
      }
    };
    const onPointerEnter = (e: PointerEvent) => {
      if (e.pointerType === "mouse") hold("hover");
    };
    const onPointerLeave = (e: PointerEvent) => {
      if (e.pointerType === "mouse") release("hover");
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse") return;
      velocity = 0;
      hold("user");
      window.clearTimeout(resumeTimer);
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") holdUntilIdle();
    };
    const onFocusIn = () => hold("focus");
    const onFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node | null)) release("focus");
    };
    const onVisibility = () => {
      if (document.hidden) hold("hidden");
      else release("hidden");
    };

    // The intersection observer reports its initial state asynchronously;
    // start held so nothing moves until the track is known to be on screen.
    hold("offscreen");
    measure();
    syncMotionHold();
    onVisibility();

    const resize = new ResizeObserver(measure);
    resize.observe(el);
    const visible = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) release("offscreen");
      else hold("offscreen");
    });
    visible.observe(el);

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("pointerenter", onPointerEnter);
    el.addEventListener("pointerleave", onPointerLeave);
    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("pointerup", onPointerUp, { passive: true });
    el.addEventListener("pointercancel", onPointerUp, { passive: true });
    el.addEventListener("focusin", onFocusIn);
    el.addEventListener("focusout", onFocusOut);
    document.addEventListener("visibilitychange", onVisibility);
    reduceMotion.addEventListener("change", syncMotionHold);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(resumeTimer);
      resize.disconnect();
      visible.disconnect();
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("pointerenter", onPointerEnter);
      el.removeEventListener("pointerleave", onPointerLeave);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("focusin", onFocusIn);
      el.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("visibilitychange", onVisibility);
      reduceMotion.removeEventListener("change", syncMotionHold);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed, direction, ...deps]);

  return { trackRef };
}

interface MarqueeTrackProps<T>
  extends Omit<React.HTMLAttributes<HTMLUListElement>, "children"> {
  trackRef: React.RefObject<HTMLUListElement | null>;
  items: readonly T[];
  keyOf: (item: T) => string;
  /** `repeat` is true for the extra copies; give their links `tabIndex={-1}`. */
  renderItem: (item: T, repeat: boolean) => React.ReactNode;
  itemClassName?: string;
  /**
   * Times the list is rendered. Three lets the loop wrap invisibly on
   * viewports up to twice as wide as one copy of the list.
   */
  copies?: number;
}

/**
 * The `<ul>` for `useMarquee`: the items repeated `copies` times, each
 * copy's first item marked so the hook can measure one copy's width. The
 * repeats exist only to make the loop seamless, so they are hidden from
 * assistive tech (they stay clickable, since any copy may be on screen).
 */
export function MarqueeTrack<T>({
  trackRef,
  items,
  keyOf,
  renderItem,
  itemClassName,
  copies = 3,
  className,
  ...rest
}: MarqueeTrackProps<T>) {
  return (
    <ul ref={trackRef} className={cn("marquee-track", className)} {...rest}>
      {Array.from({ length: copies }, (_, copy) =>
        items.map((item, index) => (
          <li
            key={`${copy}:${keyOf(item)}`}
            className={itemClassName}
            data-loop-start={index === 0 ? "" : undefined}
            aria-hidden={copy > 0 || undefined}
          >
            {renderItem(item, copy > 0)}
          </li>
        )),
      )}
    </ul>
  );
}

interface SliderArrowsProps {
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Noun for the accessible labels, e.g. "properties". */
  label: string;
  className?: string;
}

/** Right-aligned prev/next pair; 44px targets on every breakpoint. */
export function SliderArrows({
  canPrev,
  canNext,
  onPrev,
  onNext,
  label,
  className,
}: SliderArrowsProps) {
  return (
    <div className={cn("flex justify-end gap-2", className)}>
      <button
        type="button"
        onClick={onPrev}
        disabled={!canPrev}
        aria-label={`Previous ${label}`}
        className="slider-arrow"
      >
        <ChevronLeft className="size-5" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        aria-label={`Next ${label}`}
        className="slider-arrow"
      >
        <ChevronRight className="size-5" aria-hidden />
      </button>
    </div>
  );
}
